import { useTranslation } from "react-i18next";
import { CompanyLogo } from "../../components/CompanyLogo";
import { ErrorBox, FreshnessStamp, Skeleton } from "../../components/ui";
import { useAttendanceOverview } from "../../lib/api";
import { fmtNumber } from "../../lib/format";
import { SectionCard, useDateFormat } from "./shared";

/** Who is off today, who is off soon, and how the last thirty days look by division (WP-23.1). */
export default function Attendance({ company }: { company: string }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const q = useAttendanceOverview(company || undefined);
  const fmtDate = useDateFormat();

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 grid gap-4 lg:grid-cols-2">{[0, 1].map((i) => <Skeleton key={i} className="h-48" />)}</div>;
  const d = q.data;

  return (
    <div className="mt-4 space-y-4" data-hr-attendance>
      <div className="flex justify-end"><FreshnessStamp lastUpdated={d.as_of} /></div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SectionCard title={t("hr.leaveTitle")}>
          <div className="grid grid-cols-2 gap-3 p-4 text-sm">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("hr.onLeaveToday")}</div>
              {d.leave.on_leave_today.length === 0 ? <div className="text-brand-grey">–</div> : d.leave.on_leave_today.slice(0, 8).map((l) => (
                <div key={l.employee + l.from_date} className="truncate">{l.employee_name}
                  <span className="text-xs text-brand-grey"> · {l.leave_type} → {fmtDate(l.to_date)}</span></div>
              ))}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("hr.upcoming")}</div>
              {d.leave.upcoming.length === 0 ? <div className="text-brand-grey">–</div> : d.leave.upcoming.slice(0, 8).map((l) => (
                <div key={l.employee + l.from_date} className="truncate">{fmtDate(l.from_date)}
                  <span className="text-xs text-brand-grey"> · {l.employee_name} · {l.leave_type}</span></div>
              ))}
            </div>
          </div>
          <div className="border-t border-brand-grey-light px-4 py-3 text-xs">
            <div className="mb-1 text-[11px] uppercase tracking-wide text-brand-grey">{t("hr.leaveByType")}</div>
            <div className="flex flex-wrap gap-2">
              {d.leave.by_type_180d.map((r) => (
                <span key={r.leave_type} className="rounded bg-[#F6F8FA] px-2 py-1">
                  {r.leave_type}: <b>{r.n}</b> · {fmtNumber(r.days, lng, 0)} {t("units.days")}
                </span>
              ))}
            </div>
          </div>
        </SectionCard>

        <SectionCard title={t("hr.attendance")} aside={t("hr.last30")}>
          {d.attendance.length === 0 ? <div className="p-6 text-center text-sm text-brand-grey">–</div> : (
            <table className="w-full text-sm">
              <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
                <tr>
                  <th className="px-4 py-2">{t("exec.division")}</th>
                  <th className="px-2 py-2 text-right">{t("hr.present")}</th>
                  <th className="px-2 py-2 text-right">{t("hr.absent")}</th>
                  <th className="px-2 py-2 text-right">{t("hr.onLeave")}</th>
                  <th className="px-4 py-2 text-right">{t("hr.presenceRate")}</th>
                </tr>
              </thead>
              <tbody>
                {d.attendance.map((a) => (
                  <tr key={a.company} className="border-t border-brand-grey-light">
                    <td className="px-4 py-2"><CompanyLogo company={a.company} size={14} showName /></td>
                    <td className="px-2 py-2 text-right">{a.Present}</td>
                    <td className={`px-2 py-2 text-right ${a.Absent ? "text-status-red" : ""}`}>{a.Absent}</td>
                    <td className="px-2 py-2 text-right">{a["On Leave"]}</td>
                    <td className="px-4 py-2 text-right font-semibold">{a.present_rate != null ? `${fmtNumber(a.present_rate, lng, 1)} %` : "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
